import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertCircle, Check, ExternalLink, Loader2, Plus, RefreshCw, ShieldCheck, Trash2, X } from "lucide-react";
import { listSocialAccounts, removeSocialAccount, verifySocialAccount, startConnect, connectStatus, saveConnect } from "../lib/api";

const errText = (e, fallback) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : fallback; };
const day = (iso) => iso ? new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "";
const STATE = {
  ok: ["green", "Connected"], expiring: ["yellow", "Reconnect soon"], expired: ["hot", "Expired — reconnect"],
  revoked: ["hot", "Revoked — reconnect"], elsewhere: ["neutral", "On another computer"],
};
function Tag({ tone = "neutral", children }) { return <span className={`badge badge-${tone}`}>{children}</span>; }

// Sign in on the platform's own page, then pick which of the found profiles / pages / boards / channels to add.
function ConnectModal({ provider, me, close, onSaved }) {
  const [options, setOptions] = useState({ pages: false });
  // A sign-in that is still waiting or finished but not yet saved survives closing this window by accident: it is
  // remembered here, and picked up again the next time Connect is opened.
  const memo = `connect-${provider.key}`;
  const [state, setState] = useState(() => { try { return sessionStorage.getItem(memo); } catch { return null; } });
  const [resumed] = useState(!!state);
  const [found, setFound] = useState(null);
  const [picks, setPicks] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!state || found) return undefined;
    const started = Date.now();
    const t = setInterval(async () => {
      try {
        const s = await connectStatus(provider.key, state);
        if (s.status === "found") { clearInterval(t); setFound(s.found); setPicks(Object.fromEntries(s.found.map(f => [f.index, "me"]))); }
        else if (s.status === "error" || s.status === "expired") { clearInterval(t); setState(null); if (!resumed) setError(s.detail || "The sign-in expired — try again"); }
        else if (Date.now() - started > 10 * 60 * 1000) { clearInterval(t); setState(null); setError("The sign-in took too long — try again"); }
      } catch { /* keep waiting */ }
    }, 1500);
    return () => clearInterval(t);
  }, [state, found, provider.key, resumed]);

  useEffect(() => {
    try { state ? sessionStorage.setItem(memo, state) : sessionStorage.removeItem(memo); } catch { /* storage unavailable */ }
  }, [state, memo]);

  const begin = async () => {
    setError("");
    try {
      const r = await startConnect(provider.key, options);
      setState(r.state);
      window.open(r.url, "_blank", "noopener");
    } catch (e) { setError(errText(e, "Could not start the sign-in")); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const chosen = Object.entries(picks).filter(([, o]) => o).map(([index, owner]) => ({ index: Number(index), owner }));
      const { saved } = await saveConnect(provider.key, state, chosen);
      toast.success(`Added ${saved.map(a => a.display_name).join(", ")}`);
      setState(null); onSaved(); close();
    } catch (e) { toast.error(errText(e, "Could not add the accounts")); }
    finally { setBusy(false); }
  };

  // No click-outside-to-close here: coming back from the sign-in tab usually means clicking this page, and that
  // must not throw the sign-in away. Use the X.
  return <div className="modal-backdrop"><div className="modal modal-wide" data-testid={`connect-${provider.key}-modal`}>
    <button className="modal-close" onClick={close}><X size={18} /></button>
    <div className="modal-icon"><ShieldCheck size={19} /></div>
    <h2>Connect {provider.label}</h2>
    <p>{provider.note}</p>
    {provider.hosted && <p className="muted">{provider.label}'s own login page opens. Enter your {provider.label} username and password there — Open CRM never sees them — and click Allow.</p>}
    {!found && <>
      {provider.key === "linkedin" && <label className="verify-toggle"><input type="checkbox" checked={options.pages} onChange={e => setOptions({ ...options, pages: e.target.checked })} />
        <span>Also find company pages I'm an admin of</span><small className="muted">needs LinkedIn's Community Management API on your app</small></label>}
      {state
        ? <div className="modal-actions"><span className="muted"><Loader2 size={14} className="spin" /> Finish signing in in your browser…</span><button className="text-button" onClick={() => setState(null)}>Cancel</button></div>
        : <div className="modal-actions"><button className="primary-button" onClick={begin} data-testid={`connect-${provider.key}-start`}><ExternalLink size={14} /> Sign in with {provider.label}</button></div>}
    </>}
    {found && <>
      <p><b>Choose what to add.</b> “Mine” is yours only; “Company” can be used by everyone on the team{me.role !== "admin" ? " (admins only)" : ""}.</p>
      <div className="social-platforms" data-testid="connect-found">
        {found.map(f => <div className="social-platform" key={f.index}>
          <label className="social-check" style={{ width: "auto", flex: 1 }}>
            <input type="checkbox" checked={!!picks[f.index]} onChange={e => setPicks(p => ({ ...p, [f.index]: e.target.checked ? "me" : null }))} />
            {f.picture && <img src={f.picture} alt="" className="account-pic" />}<b>{f.display_name}</b><small className="muted">{f.platform && f.platform !== provider.key ? "Instagram account" : (provider.kinds[f.kind] || f.kind)}</small>
          </label>
          {picks[f.index] && <select value={picks[f.index]} onChange={e => setPicks(p => ({ ...p, [f.index]: e.target.value }))} data-testid={`connect-owner-${f.index}`}>
            <option value="me">Mine</option>
            {me.role === "admin" && <option value="company">Company</option>}
          </select>}
        </div>)}
      </div>
      <div className="modal-actions"><button className="primary-button" disabled={busy || !Object.values(picks).some(Boolean)} onClick={save} data-testid="connect-save">
        {busy ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Add selected</button></div>
    </>}
    {error && <div className="error-strip"><AlertCircle size={14} /> {error}</div>}
  </div></div>;
}

export default function AccountsPanel({ me, onChanged }) {
  const [data, setData] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [checking, setChecking] = useState(null);
  const load = async () => {
    try { setData(await listSocialAccounts()); onChanged?.(); } catch { toast.error("Could not load accounts"); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  if (!data) return <div className="panel" style={{ padding: 18 }}>Loading accounts…</div>;

  const check = async (a) => {
    setChecking(a.id);
    try { const r = await verifySocialAccount(a.id); (r.ok ? toast.success : toast.error)(`${a.display_name}: ${r.detail}`); }
    finally { setChecking(null); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.display_name}? Its sign-in is deleted from this computer's .env. Posts already made stay online.`)) return;
    try { await removeSocialAccount(a.id); load(); } catch (e) { toast.error(errText(e, "Could not remove")); }
  };
  const byPlatform = (key) => data.accounts.filter(a => a.platform === key);

  return <div className="panel accounts-panel" data-testid="social-accounts">
    <div className="accounts-head"><div><b>Connected accounts</b><small>Connect your own profiles{me.role === "admin" ? " and the company's pages" : ""}. Each person signs in with their own login.</small></div>
      <button className="outline-button" onClick={load}><RefreshCw size={14} /> Refresh</button></div>
    <div className="accounts-grid">
      {data.providers.map(p => <div className="account-tile" key={p.key} data-testid={`provider-${p.key}`}>
        <div className="account-tile-head"><b>{p.label}</b>
          <button className="text-button" onClick={() => setConnecting(p)} disabled={!p.configured || !p.localhost_ok || p.available === false}
            title={p.available === false ? `${p.label} isn't available yet` : !p.configured ? `Add ${p.missing.join(" and ")} in Settings` : undefined}
            data-testid={`connect-${p.key}`}><Plus size={13} /> {byPlatform(p.key).length ? "Add another" : "Connect"}</button></div>
        <small className="muted">{p.note}</small>
        {p.available === false ? <small className="social-warn">Not available yet. It will appear here as soon as it is switched on.</small>
          : p.hosted ? <small className="muted">Log in with your {p.label} account — nothing to set up.</small>
          : !p.configured && <small className="social-warn">Add your {p.label} app keys in Settings first.</small>}
        {byPlatform(p.key).map(a => {
          const [tone, label] = STATE[a.status] || STATE.ok;
          return <div className="account-row" key={a.id} data-testid={`account-${a.id}`}>
            {a.picture && <img src={a.picture} alt="" className="account-pic" />}
            <div><b>{a.display_name}</b><small>{a.owner_name}{a.kind ? ` · ${p.kinds[a.kind] || a.kind}` : ""}{a.expires_at && a.status !== "elsewhere" ? ` · until ${day(a.expires_at)}` : ""}</small></div>
            <Tag tone={tone}>{label}</Tag>
            {a.can_post && a.status !== "elsewhere" && <button className="icon-button small" title="Check it works (posts nothing)" onClick={() => check(a)} disabled={checking === a.id}>{checking === a.id ? <Loader2 size={13} className="spin" /> : <ShieldCheck size={13} />}</button>}
            {a.can_manage && <button className="icon-button small" title="Remove" onClick={() => remove(a)}><Trash2 size={13} /></button>}
          </div>;
        })}
      </div>)}
    </div>
    {data.public_url_problem && <div className="alert-strip"><AlertCircle size={14} /><span>Instagram and TikTok posts: {data.public_url_problem}</span></div>}
    {connecting && <ConnectModal provider={connecting} me={me} close={() => setConnecting(null)} onSaved={load} />}
  </div>;
}
